"use client";

import type { WhiteboardRenderState } from "@/lib/whiteboard/boardState";
import type { GapFillSpec } from "@/lib/whiteboard/spec";
import { normalizeAnswer } from "@/lib/whiteboard/spec";
import { motion } from "framer-motion";
import { ChevronDown, Languages, Lightbulb } from "lucide-react";
import React, { useState } from "react";
import { WhiteboardFocusSurface, WhiteboardProgressiveReveal } from "./WhiteboardPresentation";
import { WhiteboardWriteInText } from "./WhiteboardWriteInText";
import {
  type WhiteboardCardEventHandler,
  getWhiteboardMarkClass,
  getWhiteboardTargetMark,
  isWhiteboardTargetHighlighted,
  simpleWhiteboardTargetId,
} from "./types";

interface GapFillCardProps {
  spec: GapFillSpec;
  renderState: WhiteboardRenderState;
  onEvent?: WhiteboardCardEventHandler;
}

export const GapFillCard: React.FC<GapFillCardProps> = ({ spec, renderState, onEvent }) => {
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [solved, setSolved] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [wrongOptions, setWrongOptions] = useState<Set<string>>(new Set());
  const [showTranslation, setShowTranslation] = useState(false);

  const parts = spec.sentence.split(/_{3,}/);
  const hasBlank = parts.length > 1;
  const sentenceTargetId = simpleWhiteboardTargetId("gap_fill", "sentence");
  const sentenceMark = getWhiteboardTargetMark(spec.sentence, renderState, sentenceTargetId);
  const lastAnswerWasWrong = Boolean(selectedAnswer && wrongOptions.has(selectedAnswer) && !solved);

  const handlePick = (option: string, optionIndex: number) => {
    if (solved) return;
    const correct = normalizeAnswer(option) === normalizeAnswer(spec.answer);
    const nextAttempt = attempt + 1;
    setAttempt(nextAttempt);
    setSelectedAnswer(option);
    if (correct) {
      setSolved(true);
    } else {
      setWrongOptions((prev) => new Set(prev).add(option));
    }
    onEvent?.({
      event: "answered",
      response: option,
      correct,
      action: "selected_option",
      target: spec.sentence,
      targetId: simpleWhiteboardTargetId("gap_fill", "option", optionIndex),
      expected: spec.answer,
      resolved: correct,
      attempt: nextAttempt,
    });
  };

  return (
    <div className="space-y-4">
      {/* Sentence with the blank */}
      <WhiteboardFocusSurface resolved={solved}>
        <p
          data-whiteboard-target-id={sentenceTargetId}
          className={`text-lg sm:text-xl leading-relaxed font-medium text-[#F9F9F9] ${
            isWhiteboardTargetHighlighted(spec.sentence, renderState, sentenceTargetId)
              ? "rounded-xl bg-[#FD954F]/10 px-2 py-1 ring-1 ring-[#FD954F]/30"
              : ""
          } ${getWhiteboardMarkClass(sentenceMark)}`}
        >
          {hasBlank ? (
            <>
              <WhiteboardWriteInText text={parts[0]} inline />
              {selectedAnswer ? (
                <motion.span
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className={`mx-1 inline-block rounded-md border px-2.5 py-0.5 font-bold ${
                    solved
                      ? "border-[#22C55E]/50 bg-[#22C55E]/15 text-[#4ADE80]"
                      : lastAnswerWasWrong
                        ? "border-[#EF4444]/50 bg-[#EF4444]/10 text-[#FCA5A5]"
                        : "border-[#FD954F]/50 bg-[#FD954F]/10 text-[#FFD9B8]"
                  }`}
                >
                  {solved ? spec.answer : selectedAnswer}
                </motion.span>
              ) : (
                <span className="mx-1 inline-block w-20 sm:w-28 translate-y-1 border-b-2 border-dashed border-[#FD954F]/70" />
              )}
              <WhiteboardWriteInText
                text={parts.slice(1).join(" ___ ")}
                inline
                initialDelayMs={420}
              />
            </>
          ) : (
            <WhiteboardWriteInText text={spec.sentence} inline />
          )}
        </p>
      </WhiteboardFocusSurface>

      {spec.translation && (
        <div className="whiteboard-control-reveal">
          <button
            type="button"
            onClick={() => setShowTranslation((current) => !current)}
            className="-ml-2 inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-semibold text-[#D1D1D6] transition-colors hover:bg-white/[0.06] hover:text-[#F9F9F9] active:bg-white/[0.09]"
            aria-expanded={showTranslation}
            aria-controls={`${spec.id}-translation`}
          >
            <Languages size={15} strokeWidth={1.8} className="text-[#FD954F]" aria-hidden="true" />
            <span>{showTranslation ? "Hide translation" : "Show translation"}</span>
            <ChevronDown
              size={14}
              strokeWidth={1.8}
              aria-hidden="true"
              className={`text-[#8E8E93] transition-transform duration-200 ${
                showTranslation ? "rotate-180" : ""
              }`}
            />
          </button>
          {showTranslation && (
            <WhiteboardWriteInText
              id={`${spec.id}-translation`}
              as="p"
              text={spec.translation}
              initialDelayMs={80}
              className="mt-2 text-sm text-[#AEAEB2] italic"
            />
          )}
        </div>
      )}

      {spec.hint && attempt > 0 && !solved && (
        <p className="whiteboard-exercise-hint whiteboard-control-reveal flex items-start gap-1.5 rounded-lg border border-transparent px-2.5 py-2 text-sm leading-relaxed text-[#FD954F]/80">
          <Lightbulb size={15} className="mt-0.5 shrink-0" />
          <span>{spec.hint}</span>
        </p>
      )}

      <p className="sr-only" aria-live="polite">
        {solved ? "Correct." : lastAnswerWasWrong ? "Not quite. Try another answer." : ""}
      </p>

      {/* Options */}
      <div
        className="whiteboard-control-reveal flex flex-wrap gap-2"
        aria-label="Choose the missing word"
      >
        {spec.options.map((option, index) => {
          const optionTargetId = simpleWhiteboardTargetId("gap_fill", "option", index);
          const isWrong = wrongOptions.has(option);
          const isCorrectPick = solved && normalizeAnswer(option) === normalizeAnswer(spec.answer);
          const isHighlighted = isWhiteboardTargetHighlighted(option, renderState, optionTargetId);
          const mark = getWhiteboardTargetMark(option, renderState, optionTargetId);
          return (
            <motion.button
              key={option}
              whileTap={{ scale: 0.95 }}
              onClick={() => handlePick(option, index)}
              disabled={solved || isWrong}
              data-whiteboard-target-id={optionTargetId}
              className={`rounded-xl border px-4 py-2.5 text-base font-semibold transition-colors duration-150 ${
                isCorrectPick
                  ? "border-[#22C55E]/60 bg-[#22C55E]/20 text-[#4ADE80]"
                  : isWrong
                    ? "border-[#EF4444]/40 bg-[#EF4444]/10 text-[#FCA5A5] line-through opacity-60"
                    : isHighlighted
                      ? "whiteboard-tutor-highlight-pulse border-[#FD954F]/80 bg-[#FD954F]/20 text-[#FFD9B8]"
                      : "border-white/15 bg-white/[0.06] text-[#F9F9F9] hover:border-[#FD954F]/60 hover:bg-[#FD954F]/10"
              } ${getWhiteboardMarkClass(mark)} ${solved && !isCorrectPick ? "opacity-40" : ""}`}
              title={mark?.label}
            >
              {option}
            </motion.button>
          );
        })}
      </div>

      {/* Explanation after solving */}
      <WhiteboardProgressiveReveal show={solved && Boolean(spec.explanation)}>
        <p className="border-t border-white/10 pt-3 text-sm leading-relaxed text-[#D1D1D6]">
          {spec.explanation}
        </p>
      </WhiteboardProgressiveReveal>
    </div>
  );
};
